import React from "react";
import Router from "next/router";
import getDebug from "debug";
import uid from "uid";
import { shape, arrayOf, object, bool, string } from "prop-types";

import Layout from "@components/Layout/Layout";
import TestDrive from "@components/TestDrive/TestDrive";

import { useReferrerSelector } from "@redux/reducers/appConfig";
import { setTestDriveTypesAction } from "@redux/actions/configActions";
import { testDriveVehicleType } from "@types";
import { withTranslation } from "@lib/i18n";
import api from "@api";

import { i18n } from "@lib/i18n";
import { IS_SERVER } from "@shared/constants";
import { ROUTES } from "@shared/routing/routes";
import { universalGetLocalizedPathname } from "@lib/get-initial-props/universalGetLocalizedPathname";
import { universalGetCountry } from "@lib/get-initial-props/universalGetCountry";

const debug = getDebug("app:testDrive");

const TestDrivePage = ({ pageProps, isSSRRendered }) => {
  const {
    testDriveVehicles,
    testDriveLocations,
    testDriveTypes,
    homeTestDriveLocations,
    preDefinedTestDriveType,
  } = pageProps;

  const affiliation = useReferrerSelector();

  return (
    <Layout isGetPageNameByPathname>
      <TestDrive
        affiliation={affiliation}
        locations={testDriveLocations}
        types={testDriveTypes}
        vehicles={testDriveVehicles}
        homeTestDriveLocations={homeTestDriveLocations}
        isSSRRendered={isSSRRendered}
        preDefinedTestDriveType={preDefinedTestDriveType}
      />
    </Layout>
  );
};

TestDrivePage.getInitialProps = async ctx => {
  const { store, req, query } = ctx;
  const i18nInstance = IS_SERVER ? req.i18n : i18n;

  try {
    const [
      initialTestDriveDataResponse,
      fetchFree2moveVehiclesResponse,
    ] = await Promise.all([
      api.getInitialTestDriveData(i18nInstance.language),
      api.fetchFree2moveVehicles(),
    ]);

    const {
      testDriveLocations,
      homeTestDriveLocations,
      testDriveTypes,
    } = initialTestDriveDataResponse.data;

    const { data: testDriveVehicles } = fetchFree2moveVehiclesResponse;

    store.dispatch(
      setTestDriveTypesAction(
        testDriveTypes.sort((a, b) => Number(a.order) - Number(b.order))
      )
    );

    return {
      testDriveVehicles: testDriveVehicles.map(item => {
        const id = item.plateNumber || uid();

        return {
          _id: id,
          country: universalGetCountry(ctx),
          geoLatitude: item.spot.latitude,
          geoLongitude: item.spot.longitude,
          code: item.code,
          id,
        };
      }),

      testDriveLocations,
      testDriveTypes,
      homeTestDriveLocations,
      preDefinedTestDriveType: query.type,
      namespacesRequired: ["common"],
    };
  } catch (error) {
    const unavailablePathname = universalGetLocalizedPathname(
      ctx,
      ROUTES.UNAVAILABLE
    );

    if (IS_SERVER) {
      debug(`Couldn't fetch test drive initial data\n\n`, error);

      const { res } = ctx;

      return res.redirect(unavailablePathname);
    }

    return Router.push(ROUTES.UNAVAILABLE, unavailablePathname);
  }
};

TestDrivePage.propTypes = {
  pageProps: shape({
    testDriveVehicles: arrayOf(testDriveVehicleType).isRequired,
    testDriveLocations: arrayOf(object).isRequired,
    testDriveTypes: arrayOf(object).isRequired,
    homeTestDriveLocations: arrayOf(object).isRequired,
    preDefinedTestDriveType: string,
  }).isRequired,
  isSSRRendered: bool,
};

export default withTranslation("common")(TestDrivePage);
